/**
 * ErrorBoundary 컴포넌트
 *
 * @see #132 - CheckoutPage에 React ErrorBoundary 추가
 */

import { Component, ErrorInfo, ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { ROUTES } from '@/config/routes';
import { captureError } from '@/lib/sentry';
import { useApp } from '@/contexts/AppContext';

/**
 * ErrorBoundary UI 번역
 */
const errorTranslations = {
  ko: {
    errorTitle: '오류가 발생했습니다',
    errorDescription: '죄송합니다. 일시적인 오류가 발생했습니다.',
    tryAgainLater: '잠시 후 다시 시도해주세요.',
    retry: '다시 시도',
    goHome: '홈으로 돌아가기',
    checkoutErrorTitle: '결제 처리 중 오류가 발생했습니다',
    orderNotCompleted: '주문이 완료되지 않았습니다.',
    checkCartAndRetry: '장바구니를 확인하시고 다시 시도해주세요.',
    goToCart: '장바구니로 돌아가기',
  },
  en: {
    errorTitle: 'An error occurred',
    errorDescription: 'Sorry, a temporary error occurred.',
    tryAgainLater: 'Please try again later.',
    retry: 'Try again',
    goHome: 'Go to Home',
    checkoutErrorTitle: 'Payment processing error',
    orderNotCompleted: 'Your order was not completed.',
    checkCartAndRetry: 'Please check your cart and try again.',
    goToCart: 'Go to Cart',
  },
  es: {
    errorTitle: 'Ha ocurrido un error',
    errorDescription: 'Lo sentimos, ha ocurrido un error temporal.',
    tryAgainLater: 'Por favor intente de nuevo más tarde.',
    retry: 'Intentar de nuevo',
    goHome: 'Ir al Inicio',
    checkoutErrorTitle: 'Error en el procesamiento del pago',
    orderNotCompleted: 'Su pedido no fue completado.',
    checkCartAndRetry: 'Por favor revise su carrito e intente de nuevo.',
    goToCart: 'Ir al Carrito',
  },
} as const;

/**
 * Fallback render function 타입
 * 에러와 리셋 함수를 받아 ReactNode를 반환
 */
type FallbackRender = (props: { error: Error | null; resetError: () => void }) => ReactNode;

interface ErrorBoundaryProps {
  children: ReactNode;
  /** ReactNode 또는 render function */
  fallback?: ReactNode | FallbackRender;
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

/**
 * React ErrorBoundary 클래스 컴포넌트
 *
 * 자식 컴포넌트에서 발생하는 렌더링 에러를 캐치하고
 * 사용자 친화적인 Fallback UI를 표시합니다.
 */
export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    // Sentry로 에러 전송
    captureError(error, {
      componentStack: errorInfo.componentStack,
      errorBoundary: 'ErrorBoundary',
    });

    // 커스텀 에러 핸들러 호출
    this.props.onError?.(error, errorInfo);
  }

  /**
   * ErrorBoundary 상태 리셋
   */
  resetError = (): void => {
    this.setState({ hasError: false, error: null });
  };

  render(): ReactNode {
    if (this.state.hasError) {
      // 커스텀 fallback이 있으면 사용
      if (this.props.fallback) {
        // render function인 경우 에러와 리셋 함수 전달
        if (typeof this.props.fallback === 'function') {
          return this.props.fallback({
            error: this.state.error,
            resetError: this.resetError,
          });
        }
        // ReactNode인 경우 그대로 반환
        return this.props.fallback;
      }

      // 기본 Fallback UI
      return <DefaultErrorFallback error={this.state.error} onReset={this.resetError} />;
    }

    return this.props.children;
  }
}

/**
 * 기본 에러 Fallback UI Props
 */
interface DefaultErrorFallbackProps {
  error: Error | null;
  onReset?: () => void;
}

/**
 * 기본 에러 Fallback UI
 *
 * 에러 발생 시 사용자에게 친화적인 메시지를 표시하고
 * 홈으로 돌아가거나 다시 시도할 수 있는 버튼을 제공합니다.
 */
function DefaultErrorFallback({ error, onReset }: DefaultErrorFallbackProps) {
  const navigate = useNavigate();
  const { language } = useApp();
  const t = errorTranslations[language] || errorTranslations.en;

  const handleGoHome = () => {
    onReset?.();
    navigate(ROUTES.HOME);
  };

  const handleRetry = () => {
    onReset?.();
  };

  return (
    <div className="min-h-screen bg-muted flex items-center justify-center p-4">
      <div className="text-center max-w-[400px]">
        {/* 에러 아이콘 */}
        <div className="mb-4">
          <svg
            className="w-16 h-16 mx-auto text-destructive"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
            />
          </svg>
        </div>

        <h2 className="text-xl font-bold text-foreground mb-3">
          {t.errorTitle}
        </h2>

        <p className="text-sm text-muted-foreground mb-5">
          {t.errorDescription}
          <br />
          {t.tryAgainLater}
        </p>

        {/* 개발 환경에서만 에러 메시지 표시 */}
        {import.meta.env.DEV && error && (
          <pre className="text-left bg-destructive/10 border border-destructive/30 text-destructive p-3 rounded-lg text-xs mb-5 overflow-auto max-h-32">
            {error.message}
            {error.stack && (
              <>
                {'\n\n'}
                {error.stack}
              </>
            )}
          </pre>
        )}

        {/* 버튼 그룹 */}
        <div className="flex flex-col gap-3">
          <button
            onClick={handleRetry}
            className="w-full bg-primary text-white px-6 py-3 rounded-lg font-medium hover:bg-primary-hover transition-colors"
          >
            {t.retry}
          </button>
          <button
            onClick={handleGoHome}
            className="w-full bg-muted text-muted-foreground px-6 py-3 rounded-lg font-medium hover:bg-muted/80 transition-colors"
          >
            {t.goHome}
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * Checkout 전용 에러 Fallback UI Props
 */
interface CheckoutErrorFallbackProps {
  error: Error | null;
  onReset?: () => void;
}

/**
 * Checkout 페이지 전용 에러 Fallback UI
 *
 * 결제 과정에서 에러 발생 시 장바구니로 돌아갈 수 있는 옵션을 제공합니다.
 */
export function CheckoutErrorFallback({ error, onReset }: CheckoutErrorFallbackProps) {
  const navigate = useNavigate();
  const { language } = useApp();
  const t = errorTranslations[language] || errorTranslations.en;

  const handleGoToCart = () => {
    onReset?.();
    navigate(ROUTES.CART);
  };

  const handleGoHome = () => {
    onReset?.();
    navigate(ROUTES.HOME);
  };

  return (
    <div className="min-h-screen bg-muted flex items-center justify-center p-4">
      <div className="text-center max-w-[400px]">
        {/* 에러 아이콘 */}
        <div className="mb-4">
          <svg
            className="w-16 h-16 mx-auto text-destructive"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
        </div>

        <h2 className="text-xl font-bold text-foreground mb-3">
          {t.checkoutErrorTitle}
        </h2>

        <p className="text-sm text-muted-foreground mb-5">
          {t.orderNotCompleted}
          <br />
          {t.checkCartAndRetry}
        </p>

        {/* 개발 환경에서만 에러 메시지 표시 */}
        {import.meta.env.DEV && error && (
          <pre className="text-left bg-destructive/10 border border-destructive/30 text-destructive p-3 rounded-lg text-xs mb-5 overflow-auto max-h-32">
            {error.message}
          </pre>
        )}

        {/* 버튼 그룹 */}
        <div className="flex flex-col gap-3">
          <button
            onClick={handleGoToCart}
            className="w-full bg-primary text-white px-6 py-3 rounded-lg font-medium hover:bg-primary-hover transition-colors"
          >
            {t.goToCart}
          </button>
          <button
            onClick={handleGoHome}
            className="w-full bg-muted text-muted-foreground px-6 py-3 rounded-lg font-medium hover:bg-muted/80 transition-colors"
          >
            {t.goHome}
          </button>
        </div>
      </div>
    </div>
  );
}

export default ErrorBoundary;
